import { useState, useEffect } from 'react';
import { safeInvoke } from '../lib/safeInvoke';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { safeParseJSON } from '../lib/sanitizeDisplay';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { useI18n } from '../hooks/useI18n';

interface IChingResult {
  hexagramNumber: number;
  hexagramName: string;
  hexagramNamePt: string;
  lines: (0 | 1)[];
  changingLines: number[];
  transformedHexagram?: number;
  sections: {
    julgamento?: string;
    imagem?: string;
    linhas?: string;
    conselho?: string;
  };
  readingId: string | null;
  text: string;
}

const TRIGRAMS = ['☰', '☱', '☲', '☳', '☴', '☵', '☶', '☷'];

function HexagramDisplay({ lines }: { lines: (0 | 1)[]; changingLines?: number[] }) {
  const displayLines = [...lines].reverse();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
      {displayLines.map((line, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {line === 1 ? (
            <div style={{ width: '64px', height: '8px', backgroundColor: 'var(--petrol)', borderRadius: '2px' }} />
          ) : (
            <>
              <div style={{ width: '28px', height: '8px', backgroundColor: 'var(--petrol)', borderRadius: '2px' }} />
              <div style={{ width: '8px' }} />
              <div style={{ width: '28px', height: '8px', backgroundColor: 'var(--petrol)', borderRadius: '2px' }} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: 'julgamento', label: '⚖️ Julgamento' },
  { key: 'imagem',     label: '🖼️ Imagem' },
  { key: 'linhas',     label: '〰️ Linhas' },
  { key: 'conselho',   label: '🌟 Conselho' },
];

const LOADING_PHASES = [
  'Lançando as moedas sagradas...',
  'Identificando o hexagrama...',
  'Consultando a sabedoria ancestral do I Ching...',
  'Revelando a orientação para sua questão...',
];

export function IChingPage() {
  useSystemUsageTracker('i_ching');
  const { t, lang } = useI18n();
  const [question,  setQuestion]  = useState('');
  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<IChingResult | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('julgamento');
  const [throwing,  setThrowing]  = useState(false);

  useEffect(() => {
    if (result) { setResult(null); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  async function consult() {
    setThrowing(true);
    await new Promise(r => setTimeout(r, 1500)); // dramatic pause
    setThrowing(false);
    setLoading(true);
    setError(null);

    const { data, fromFallback } = await safeInvoke<IChingResult>('i-ching', {
      body:     { question: (question ?? '').trim() || undefined },
      fallback: { hexagramNumber: 0, hexagramName: '', hexagramNamePt: '', lines: [1,1,1,1,1,1], changingLines: [], sections: {}, readingId: null, text: '' },
      timeout:  30_000,
    });

    setLoading(false);
    if (fromFallback || !data.text) {
      setError(t('pages.iching.error'));
      return;
    }
    // If sections are empty but text contains JSON, parse client-side
    let sections = data.sections;
    let text = data.text;
    if (!sections?.julgamento) {
      const parsed = safeParseJSON<{ julgamento?: string; imagem?: string; linhas?: string; conselho?: string; sintese?: string }>(text);
      if (parsed?.julgamento) {
        sections = { julgamento: parsed.julgamento, imagem: parsed.imagem ?? '', linhas: parsed.linhas ?? '', conselho: parsed.conselho ?? '' };
        text = parsed.sintese ?? parsed.julgamento;
      } else if (text.trimStart().startsWith('{')) {
        text = 'O oráculo está em contemplação. Tente novamente ✦';
      }
    }
    setResult({ ...data, sections, text });
    setActiveTab('julgamento');
  }

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.iching.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.iching.subtitle')}
        </p>
      </div>

      {!result && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1rem', lineHeight: 1.6 }}>
            {t('pages.iching.intro')}
          </p>

          {/* Example questions */}
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem', fontWeight: 600 }}>
              Exemplos de perguntas:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {[
                'Devo aceitar esta oportunidade agora?',
                'Como posso avançar nesta situação?',
                'O que preciso liberar para seguir em frente?',
                'Qual é a energia deste relacionamento?',
              ].map(ex => (
                <button
                  key={ex}
                  onClick={() => setQuestion(ex)}
                  style={{
                    fontSize: 'var(--text-caption)',
                    color: 'var(--petrol)',
                    backgroundColor: 'rgba(245,200,66,0.08)',
                    border: '1px solid rgba(245,200,66,0.2)',
                    borderRadius: 'var(--radius-full)',
                    padding: '0.2rem 0.6rem',
                    cursor: 'pointer',
                    background: 'rgba(245,200,66,0.08)',
                    transition: 'background-color 0.15s',
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="ichq">
              {t('pages.iching.questionLabel')} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t('pages.onboarding.optional')}</span>
            </label>
            <textarea id="ichq" className="form-textarea" value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder={t('pages.iching.questionPlaceholder')}
              rows={3} maxLength={300} />
          </div>

          <button className="btn btn-primary btn-block" onClick={consult} disabled={loading || throwing}>
            {throwing ? t('pages.iching.throwingCoins') : t('pages.iching.consultCta')}
          </button>
        </div>
      )}

      {throwing && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {TRIGRAMS.map((t, i) => (
              <span key={i} style={{ display: 'inline-block', animation: `spin 0.${i + 3}s linear infinite`, margin: '0 4px' }}>{t}</span>
            ))}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>{t('pages.iching.throwingMsg')}</p>
        </div>
      )}

      {loading && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={consult}>
            Tentar novamente
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {/* Hexagram display */}
          <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <HexagramDisplay lines={result.lines} changingLines={result.changingLines} />
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', color: 'var(--text-primary)', marginTop: '1rem', marginBottom: '0.25rem' }}>
              Hexagrama {result.hexagramNumber}, {result.hexagramNamePt}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {result.hexagramName}
            </p>
            {result.transformedHexagram && (
              <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-caption)', marginTop: '0.5rem' }}>
                Transforma-se em: Hexagrama {result.transformedHexagram}
              </p>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', marginBottom: '1rem', paddingBottom: '0.25rem' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={activeTab === t.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="card animate-fade-in" key={activeTab} style={{ marginBottom: '1.5rem' }}>
            <div className="reading-text">
              {((result.sections as Record<string, string>)[activeTab] || result.text)
                .split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>

          <ReadingActions pageName="I Ching" reading={result.text} signEmoji="☯️" />
          <ReadingFeedback feature="i-ching" readingId={result.readingId ?? undefined} />

          <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={() => { setResult(null); setQuestion(''); }}>
            {t('pages.iching.newConsult')}
          </button>
        </div>
      )}
    </div>
  );
}
